import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, Send, Save, MessageSquare,
  AlertTriangle, CheckCircle2, User, MapPin, Phone, Mail, Wifi,
  Eye, EyeOff, Copy,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Full-page expanded ticket view — same underlying data as the Kanban
// drawer (single /commissions/migrations/clients/:id source of truth),
// but laid out for focused work: bigger activity timeline, a proper
// canned-response composer with live preview, and NBN credentials on
// the drawer for the eventual handover flow.

export default function AdminTicketDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [activity, setActivity] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [board, setBoard] = useState(null);

  async function load() {
    try {
      const { data: c } = await api.get(`/commissions/migrations/clients/${id}`);
      setClient(c);
      const [{ data: act }, { data: b }] = await Promise.all([
        api.get(`/commissions/migrations/clients/${id}/activity`),
        api.get(`/commissions/migrations/${c.partner_code}`).catch(() => ({ data: null })),
      ]);
      setActivity(act.activity || []);
      setBoard(b);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load ticket");
    }
  }
  useEffect(() => { load(); }, [id]);

  async function saveField(field, value) {
    if (!client) return;
    try {
      const { data } = await api.patch(`/commissions/migrations/clients/${client.id}`, { [field]: value });
      setClient(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  }

  async function addNote() {
    return _postNote({ notify: null });
  }
  // v1.10.51 — restored "Post + Email Partner" / "Post + Email Customer"
  // buttons on the ticket composer (previously the only send options
  // lived in the deprecated CannedResponseComposer which has been
  // removed). ``notify`` decides which downstream email endpoint fires
  // after the note is saved. On failure of the email step the note is
  // still kept — the user can retry via the per-row Email button on
  // the Kanban drawer.
  async function postAndEmail(who) {
    return _postNote({ notify: who });
  }
  async function _postNote({ notify }) {
    if (!noteBody.trim()) return;
    setBusy(true);
    try {
      const { data: created } = await api.post(
        `/commissions/migrations/clients/${client.id}/notes`,
        { body: noteBody });
      const activityId = created?.activity_id || created?.id;
      if (notify === "partner" && activityId) {
        try {
          await api.post(`/commissions/migrations/clients/${client.id}/activity/${activityId}/email-partner`);
          toast.success("Note posted & partner emailed");
        } catch (e) {
          toast.warning("Note saved — partner email failed. Retry from the activity row.");
        }
      } else if (notify === "customer" && activityId) {
        try {
          await api.post(`/commissions/migrations/clients/${client.id}/activity/${activityId}/email-customer`);
          toast.success("Note posted & customer emailed");
        } catch (e) {
          toast.warning("Note saved — customer email failed. Retry from the activity row.");
        }
      } else {
        toast.success("Note posted");
      }
      setNoteBody("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Note failed");
    } finally { setBusy(false); }
  }

  if (!client) return <div className="p-10 text-slate-500">Loading ticket…</div>;

  const mismatch = client.plan_mismatch || {};
  const snap = client.matched_plan_snapshot || {};

  return (
    <div className="space-y-5 pb-16" data-testid={`ticket-detail-${client.id}`}>
      <div className="flex items-center gap-4 flex-wrap">
        <Link to="/admin/tickets" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300"><ArrowLeft className="w-3 h-3" /> All tickets</Link>
        <span className="text-slate-600">·</span>
        <Link to={`/admin/onboarding/${client.partner_code}`} className="text-xs text-slate-400 hover:text-cyan-300">Kanban view</Link>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">
            {client.partner_code} · {(client.stage || "").replace(/_/g, " ")}
          </div>
          <h1 className="font-display text-3xl tracking-tight mt-1">{client.business_name || client.contact_name || client.email}</h1>
          {client.linked_signup_id && (
            <div className="text-xs text-emerald-300 mt-1 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Signed the online transfer form</div>
          )}
        </div>
        {client.syncro_ticket_id && (
          <a href={`https://st.syncromsp.com/tickets/${client.syncro_ticket_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-cyan-300 hover:text-cyan-200 border border-slate-800 rounded-lg px-3 py-2">
            Syncro #{client.syncro_ticket_number} <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      {mismatch.is_mismatch && !mismatch.acknowledged && (
        <div className="rounded-lg border-2 border-red-500/60 bg-red-500/10 p-4 flex items-start gap-3" data-testid="ticket-mismatch-banner">
          <AlertTriangle className="w-5 h-5 text-red-300 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest font-bold text-red-200 mb-1">Plan mismatch — review before activation</div>
            <ul className="text-xs text-red-200/90 space-y-1 list-disc pl-5">
              {(mismatch.reasons || []).map((r, i) => <li key={`${i}-${r}`}>{r}</li>)}
            </ul>
          </div>
        </div>
      )}

      <div className="space-y-5">
          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Customer</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <TicketField label="Business" icon={<User className="w-3.5 h-3.5" />} value={client.business_name} onSave={v => saveField("business_name", v)} />
              <TicketField label="Contact" icon={<User className="w-3.5 h-3.5" />} value={client.contact_name} onSave={v => saveField("contact_name", v)} />
              <TicketField label="Email" icon={<Mail className="w-3.5 h-3.5" />} value={client.email} onSave={v => saveField("email", v)} />
              <TicketField label="Phone" icon={<Phone className="w-3.5 h-3.5" />} value={client.phone} onSave={v => saveField("phone", v)} />
              <TicketField label="Address" icon={<MapPin className="w-3.5 h-3.5" />} value={client.service_address} onSave={v => saveField("service_address", v)} span={2} />
              <TicketField label="ABN" value={client.abn} onSave={v => saveField("abn", v)} />
              <TicketField label="External ID" value={client.external_customer_id} onSave={v => saveField("external_customer_id", v)} />
            </div>
          </section>

          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4 inline-flex items-center gap-2"><Wifi className="w-3.5 h-3.5" /> Plan &amp; NBN credentials</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <TicketField label="Current plan (imported)" value={client.current_plan_name} onSave={v => saveField("current_plan_name", v)} />
              <TicketField label="Current fee" value={client.current_monthly_fee} onSave={v => saveField("current_monthly_fee", v)} />
              <TicketField label="Chosen plan (signup)" value={snap.name || client.chosen_plan} readOnly />
              <TicketField label="Chosen fee" value={snap.monthly_price ? `$${Number(snap.monthly_price).toFixed(2)}` : ""} readOnly />
              <TicketField label="NBN username" value={client.nbn_username} onSave={v => saveField("nbn_username", v)} placeholder="From NBN provider handover" mono />
              <TicketField label="NBN password" value={client.nbn_password} onSave={v => saveField("nbn_password", v)} placeholder="From NBN provider handover" mono secret />
              <TicketField label="NBN speed" value={client.nbn_speed} onSave={v => saveField("nbn_speed", v)} placeholder="e.g. 100/40 Mbps" />
              <TicketField label="WSM service ID" value={client.nbn_wsm_service_id} onSave={v => saveField("nbn_wsm_service_id", v)} placeholder="e.g. WSM-99887766" mono />
              <TicketField label="UNI-D port" value={client.nbn_unid_port} onSave={v => saveField("nbn_unid_port", v)} placeholder="e.g. 1" />
            </div>
            {(client.nbn_username || client.nbn_password) && (
              <div className="mt-3 text-[11px] text-amber-300 inline-flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Sensitive — password is stored encrypted at rest. Only reveal it when sharing with the customer.</div>
            )}
          </section>

          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> Add a note</h2>
            <textarea
              rows={3} value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
              placeholder="Save as an internal note, or click a button on the right to also email the partner or the customer."
              className="w-full bg-slate-950 border border-slate-800 focus:border-cyan-500 outline-none rounded-md px-3 py-2 text-sm"
              data-testid="ticket-note-body"
            />
            <div className="mt-2 flex flex-wrap justify-end gap-2">
              <button onClick={addNote} disabled={busy || !noteBody.trim()} data-testid="ticket-note-send" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-950 text-slate-200 text-sm font-medium disabled:opacity-60">
                <MessageSquare className="w-4 h-4" /> Save note
              </button>
              <button onClick={() => postAndEmail("partner")} disabled={busy || !noteBody.trim()} data-testid="ticket-note-send-partner" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium disabled:opacity-60">
                <Send className="w-4 h-4" /> Save &amp; email partner
              </button>
              <button onClick={() => postAndEmail("customer")} disabled={busy || !noteBody.trim() || !client.email} data-testid="ticket-note-send-customer" title={!client.email ? "Customer has no email on file" : "Send this note to the customer"} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-medium disabled:opacity-60">
                <Send className="w-4 h-4" /> Save &amp; email customer
              </button>
            </div>
          </section>

          {/* Activity feed — moved beneath the composer in v1.10.129
              so the ticket layout matches the Kanban drawer flow
              (type note → see the new row appear immediately below).
              Previously lived in a right-hand sidebar which split the
              operator's eye-line between composer and timeline. */}
          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5" /> Activity
              {activity.length > 0 && <span className="text-slate-600 normal-case tracking-normal">· {activity.length}</span>}
            </h2>
            {activity.length === 0 ? (
              <div className="text-sm text-slate-500">No activity yet — your first note will land here.</div>
            ) : (
              <ul className="space-y-3 max-h-[600px] overflow-y-auto pr-2" data-testid="ticket-activity-list">
                {activity.map(a => (
                  <li key={a.id} className="text-xs border-l-2 border-slate-800 pl-3 pb-3" data-testid={`ticket-activity-row-${a.id}`}>
                    <div className="text-slate-500 mb-0.5">
                      <span className="text-slate-300">{a.actor_email}</span>
                      <span> · </span>
                      <span>{new Date(a.created_at).toLocaleString(undefined, {month:"short", day:"2-digit", hour:"2-digit", minute:"2-digit"})}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">{a.action.replace(/_/g, " ")}</div>
                    {a.details?.body && <div className="text-slate-200 whitespace-pre-wrap mt-1">{a.details.body}</div>}
                    {a.details?.from && <div className="text-slate-400 mt-1">{a.details.from} → <span className="text-cyan-300">{a.details.to}</span></div>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
    </div>
  );
}

function TicketField({ label, value, onSave, icon, readOnly, span = 1, placeholder = "", mono = false, secret = false }) {
  const [v, setV] = useState(value || "");
  const [dirty, setDirty] = useState(false);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => { setV(value || ""); setDirty(false); }, [value]);
  const isSecretHidden = secret && !revealed && !dirty;
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-1.5">{icon}{label}</label>
      <div className="mt-1 flex gap-1.5">
        <input
          type={isSecretHidden ? "password" : "text"}
          value={v} disabled={readOnly}
          onChange={(e) => { setV(e.target.value); setDirty(true); }}
          placeholder={placeholder}
          className={`flex-1 bg-slate-950 border border-slate-800 focus:border-cyan-500 outline-none rounded-md px-3 py-1.5 text-sm disabled:opacity-70 ${mono ? "font-mono" : ""}`}
        />
        {secret && v && (
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            className="px-2 rounded-md border border-slate-800 bg-slate-950 hover:bg-slate-800 text-slate-400 hover:text-slate-100"
            title={revealed ? "Hide password" : "Show password"}
            data-testid={`ticket-secret-toggle-${label.toLowerCase().replace(/\s+/g,"-")}`}
          >
            {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        )}
        {v && !dirty && (
          <button
            type="button"
            onClick={async () => { try { await navigator.clipboard.writeText(v); toast.success(`${label} copied`); } catch { /* ignore */ } }}
            className="px-2 rounded-md border border-slate-800 bg-slate-950 hover:bg-slate-800 text-slate-400 hover:text-slate-100"
            title={`Copy ${label}`}
            data-testid={`ticket-copy-${label.toLowerCase().replace(/\s+/g,"-")}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
        {dirty && !readOnly && (
          <button onClick={() => { onSave(v); setDirty(false); }} className="px-2 rounded-md bg-cyan-500 hover:bg-cyan-400 text-white text-xs" title="Save">
            <Save className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function CannedResponseComposer_removed() { return null; }
